import { useQueryClient } from 'react-query'

import { useQueryApi, useMutationApi } from 'utils/api'
import {
  handleError,
  validateStatus,
} from 'api/kinerja/pengawas-sekolah/config'
import { apiPaths, queryKeys } from './config'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { AxiosError } from 'axios'
import type {
  PeriodikPegawaiItem,
  ResponseGetPeriodikList,
  ResponseGetPeriodikPegawaiList,
  ParamsGetPeriodikPegawai,
  RequestSendPeriodikRatingToEkin,
  RequestSavePeriodikRatingPegawai,
} from 'types/kinerja-tim-penilai-ps'
import type { UseQueryOptions, UseMutationOptions } from 'react-query'
import type { CustomError } from 'utils/customError'

export function useAPIGetPenilaianPeriodik(
  options: UseQueryOptions<ResponseGetPeriodikList> = {}
) {
  const { period } = usePeriodContext()
  return useQueryApi<ResponseGetPeriodikList>(
    apiPaths.penilaianPeriodik(period.id),
    {
      queryKey: [
        queryKeys.API_GET_PENILAIAN_PERIODIK,
        {
          period: period.id,
        },
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPenilaianPeriodikPegawai({
  params,
  options,
}: {
  params: ParamsGetPeriodikPegawai
  options?: UseQueryOptions<ResponseGetPeriodikPegawaiList>
}) {
  const { period } = usePeriodContext()
  const { month } = params

  return useQueryApi<ResponseGetPeriodikPegawaiList>(
    `${apiPaths.penilaianPeriodik(period.id)}/${month}`,
    {
      queryKey: [
        queryKeys.API_GET_PENILAIAN_PERIODIK_PEGAWAI,
        {
          period: period.id,
          month,
        },
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPenilaianPeriodikPegawaiDetail({
  params,
  options,
}: {
  params: ParamsGetPeriodikPegawai
  options?: UseQueryOptions<PeriodikPegawaiItem>
}) {
  const { period } = usePeriodContext()
  const { month, pengawasSekolahId } = params

  return useQueryApi<PeriodikPegawaiItem>(
    `${apiPaths.penilaianPeriodik(period.id)}/${month}/ps/${pengawasSekolahId}`,
    {
      queryKey: [
        queryKeys.API_GET_PENILAIAN_PERIODIK_PEGAWAI_DETAIL,
        {
          period: period.id,
          month,
          pengawasSekolahId,
        },
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPISavePenilaianPeriodikPegawai({
  params,
  options,
}: {
  params: ParamsGetPeriodikPegawai
  options?: UseMutationOptions<
    unknown,
    CustomError,
    RequestSavePeriodikRatingPegawai
  >
}) {
  const { period } = usePeriodContext()
  const { month } = params

  return useMutationApi<unknown, CustomError, RequestSavePeriodikRatingPegawai>(
    `${apiPaths.penilaianPeriodik(period.id)}/${month}`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...options,
      },
    }
  )
}

export function useAPISendPenilaianPeriodikToEkin({
  params,
  options,
}: {
  params: ParamsGetPeriodikPegawai
  options?: UseMutationOptions<
    unknown,
    CustomError,
    RequestSendPeriodikRatingToEkin
  >
}) {
  const { period } = usePeriodContext()
  const { month } = params

  return useMutationApi<unknown, CustomError, RequestSendPeriodikRatingToEkin>(
    `${apiPaths.penilaianPeriodik(period.id)}/${month}/send-to-ekin`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...options,
      },
    }
  )
}

export function useApiResetPenilaianPeriodik({
  month,
  pengawasSekolahId,
}: {
  month: number
  pengawasSekolahId: string
}) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<{ message: string }, AxiosError, {}>(
    `${apiPaths.penilaianPeriodik(
      period.id
    )}/${month}/ps/${pengawasSekolahId}/reset`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PENILAIAN_PERIODIK_PEGAWAI_DETAIL,
            {
              period: period.id,
              month,
              pengawasSekolahId,
            },
          ])
        },
      },
    }
  )
}
